import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  Search, Filter, MoreHorizontal, Eye, Building2, Download, Plus,
  Home, Briefcase, User, Car, BookOpen,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { exportLoans, getAgentLoans } from '../../lib/api';
import { toast } from 'sonner';
import ExportDialog from '../../components/common/ExportDialog';

const statusColors = {
  'Under Processing': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'Resubmission Required': 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
  Approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Disbursed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  Rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  Hold: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

const loanTypeLabelMap = {
  home: 'Home Loan',
  business: 'Business Loan',
  personal: 'Personal Loan',
  car: 'Car Loan',
  education: 'Education Loan',
  other: 'Other',
};

const mapApiLoanToUi = (loan) => ({
  id: loan.id,
  applicantName: loan.applicant_name,
  loanType: loanTypeLabelMap[loan.loan_type] || loan.loan_type || 'Loan',
  bank: loan.bank_name || loan.product_details?.bank_name || '-',
  amount: loan.loan_amount || 0,
  emi: loan.emi || 0,
  status: loan.status || 'Under Processing',
  stage: loan.stage || 'submitted',
  // Keep original fields for the view modal
  ...loan,
});

const LoansTable = ({ title, description, initialStatusFilter = 'all' }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [apiLoans, setApiLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!token) return;

    let isMounted = true;
    const fetchLoans = async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await getAgentLoans(token);
        if (isMounted) setApiLoans(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error('Failed to fetch agent loans', err);
        if (isMounted) {
          setError(err.message || 'Failed to fetch loans');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLoans();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const effectiveLoans = useMemo(
    () => apiLoans.map(mapApiLoanToUi),
    [apiLoans],
  );

  const filteredLoans = effectiveLoans.filter((loan) => {
    const matchesSearch =
      loan.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || loan.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const handleExport = async ({ format, startDate, endDate }) => {
    if (!token) return;
    setExporting(true);
    try {
      await exportLoans(token, {
        format,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        q: searchTerm.trim() || undefined,
        startDate,
        endDate,
      });
      toast.success(`Loan export downloaded (${format.toUpperCase()})`);
      setShowExportDialog(false);
    } catch (err) {
      toast.error(err?.message || 'Failed to export loans');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        title="Export Loans"
        description="Select format and date range before exporting loan data."
        onSubmit={handleExport}
        loading={exporting}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {title || 'Loan Applications'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {description || 'Track the status of all your filed loan applications'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate('/agent/apply/products')}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white gap-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add New Loan</span>
          </Button>
          <Button variant="outline" onClick={() => setShowExportDialog(true)}>
            <Download className="h-4 w-4 mr-2" />Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by loan ID or applicant name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {['Under Processing', 'Resubmission Required', 'Approved', 'Disbursed', 'Rejected', 'Hold'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File ID</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Loan Type</TableHead>
                  <TableHead>Bank / NBFC</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>EMI</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLoans.map((loan) => (
                  <TableRow key={loan.id} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="font-mono text-sm">{loan.id}</TableCell>
                    <TableCell className="font-medium">{loan.applicantName}</TableCell>
                    <TableCell>{loan.loanType}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />{loan.bank}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(loan.amount)}</TableCell>
                    <TableCell>{loan.emi > 0 ? formatCurrency(loan.emi) : '-'}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[loan.status]}>{loan.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{loan.stage}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem
                            onClick={() => navigate(`/agent/loans/${loan.id}`, { state: { loan } })}
                            className="gap-2 cursor-pointer"
                          >
                            <Eye className="h-4 w-4 text-blue-500" /><span>View Details</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading your loan files...</p>
            </div>
          )}
          {!loading && filteredLoans.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No loan files found.</p>
            </div>
          )}
          {error && (
            <div className="text-center py-4">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Exported pages ──────────────────────────────────────────────────────────
// Note: Agents can only VIEW their filed loans, not edit/delete (admin does that)
export const AgentLoans = () => <LoansTable />;
export const ProcessingLoans = () => <LoansTable title="Under Processing" description="Your files currently being processed" initialStatusFilter="Under Processing" />;
export const ApprovedLoans = () => <LoansTable title="Approved Loans" description="Your loan files that are approved" initialStatusFilter="Approved" />;
export const DisbursedLoans = () => <LoansTable title="Disbursed Loans" description="Loans that have been disbursed" initialStatusFilter="Disbursed" />;
export const RejectedLoans = () => <LoansTable title="Rejected Loans" description="Your files that were rejected" initialStatusFilter="Rejected" />;
export const HoldLoans = () => <LoansTable title="Hold Cases" description="Files currently on hold" initialStatusFilter="Hold" />;

export default AgentLoans;
